/**
 * Typed-operator query parser for the lane omnibox (Tasks Rebuild Task 13).
 *
 * Parses a single free-typed search string into structured "chips" (one per
 * recognised `key:value` operator) plus a `freeText` remainder that becomes
 * the plain `q` (FTS/prefix) param. Pure and synchronous -- no URL/router
 * dependency here (see Omnibox.tsx for how chips get applied to
 * laneUrlState) and no server calls, so it is exhaustively unit-testable in
 * isolation.
 *
 * Supported operators (spec.md Part 3 / task-13-brief.md):
 *   from:<sender>        -- sender email/name filter (maps to senderEmail)
 *   lender:<name>        -- ALIAS of from: -- same chip `key: 'from'`, not a
 *                            separate concept (task-13-brief.md is explicit
 *                            that lender: "maps to the same senderEmail
 *                            param as from:").
 *   claim:<ref>
 *   since:<date>          -- UK DD/MM or DD/MM/YYYY -> receivedFrom.
 *   until:<date>          -- same UK date parsing -> receivedTo (#4). The
 *                            backend (python-backend/app/services/
 *                            task_lane_service.py's _build_common_filters)
 *                            treats it as end-of-day inclusive for the given
 *                            day (`received_date < until + 1 day`), the
 *                            natural complement of since:'s inclusive `>=`.
 *                            Combines with `since:` into a range. Like
 *                            `since:`/`assigned:`, it is a genuine no-op
 *                            under `archived:yes` (archived_email_tasks has
 *                            no received_date column) -- Omnibox.tsx mutes
 *                            the chip in that case rather than marking it
 *                            permanently unsupported.
 *   assigned:<name|unassigned>
 *   status:<pending|completed> -- switches the Pending/Completed sub-tab
 *                            (laneUrlState's `tab`), not a query-param
 *                            filter -- Omnibox.tsx maps this chip to
 *                            `applyTab`, not `applyFilter`.
 *   archived:yes           -- only the literal 'yes' is recognised (matches
 *                            the existing applyArchived boolean convention).
 *
 * Quoted values: `from:"John Smith"` -- the value may contain spaces when
 * wrapped in double quotes.
 *
 * Anything that isn't a recognised `key:value` pair -- including an
 * unknown operator prefix (`foo:bar`) and a recognised operator with an
 * unparseable/invalid value (`since:31/13/2026`, `status:bogus`) --
 * accumulates verbatim into `freeText` rather than being silently dropped
 * or raising. This mirrors the existing UK-date gotcha documented
 * elsewhere in this app (Proclaim/Cosmos DD/MM/YYYY strings): dates are
 * parsed by hand below, never via `new Date(rawString)`, which would parse
 * a UK string as US MM/DD and silently swap day/month.
 */

export type OmniChipKey = 'from' | 'claim' | 'since' | 'until' | 'assigned' | 'status' | 'archived';

export interface OmniChip {
  key: OmniChipKey;
  /** Normalised value: ISO `YYYY-MM-DD` for since/until, lowercased
   * 'yes'/'pending'/'completed'/'unassigned' sentinels where applicable,
   * trimmed text otherwise. This is what Omnibox.tsx writes into the URL. */
  value: string;
  /** The original matched token text (e.g. `since:1/7`, `from:"John Smith"`)
   * -- used for the free-text fallback reconstruction and available for
   * display/debugging. */
  raw: string;
  /** True for every recognised chip, `until` included (#4: the backend now
   * has a receivedTo predicate) -- kept on the type since Omnibox.tsx's
   * muting under `archived:yes` is a display-layer decision made from URL
   * state, not from this parse-time flag. */
  supported: boolean;
}

/** A whitespace-delimited token whose key matched a recognised operator
 * (`since`/`until`/`status`/`archived`/...) but whose value failed
 * validation, so it fell into `freeText` rather than becoming a chip. The
 * omnibox surfaces these as inline "malformed operator" feedback (Item 13)
 * so a mistyped `since:31/13` or `status:bogus` isn't silently treated as a
 * free-text search term. `key` is the normalised chip key the alias maps to
 * (e.g. `lender:` -> `from`). */
export interface InvalidToken {
  raw: string;
  key: OmniChipKey;
}

export interface ParsedOmniQuery {
  chips: OmniChip[];
  freeText: string;
  invalidTokens: InvalidToken[];
}

export interface ParseOmniQueryOptions {
  /** Reference "now" used to fill in an omitted year on a DD/MM `since:`/
   * `until:` value. Defaults to `new Date()`; tests pin this for
   * determinism (see aging.ts's identical `now` parameter idiom). */
  now?: Date;
}

const KEY_ALIASES: Record<string, OmniChipKey> = {
  from: 'from',
  lender: 'from',
  claim: 'claim',
  since: 'since',
  until: 'until',
  assigned: 'assigned',
  status: 'status',
  archived: 'archived',
};

/** Matches, in order of preference at each position: a `key:"quoted value"`
 * pair, a `key:nonSpaceValue` pair, or a bare non-space word. `String.
 * matchAll` walks these left-to-right and naturally skips whitespace
 * between matches, so free-text words and operators can be interleaved in
 * any order. */
const TOKEN_RE = /([A-Za-z]+):"([^"]*)"|([A-Za-z]+):(\S+)|(\S+)/g;

/**
 * Parses a UK-formatted `DD/MM` or `DD/MM/YYYY` date string by hand
 * (never `new Date(rawString)` -- see the project's documented UK-date
 * gotcha: that would parse the string as US `MM/DD` and silently swap day
 * and month). Returns an ISO `YYYY-MM-DD` string, or `null` if the value
 * isn't a well-formed, calendar-valid date.
 */
function parseUkDate(raw: string, now: Date): string | null {
  const parts = raw.split('/');
  if (parts.length !== 2 && parts.length !== 3) return null;

  const [dStr, mStr, yStr] = parts;
  if (!/^\d{1,2}$/.test(dStr) || !/^\d{1,2}$/.test(mStr)) return null;

  const day = parseInt(dStr, 10);
  const month = parseInt(mStr, 10);
  if (month < 1 || month > 12) return null;

  let year: number;
  if (yStr !== undefined) {
    if (!/^\d{4}$/.test(yStr)) return null;
    year = parseInt(yStr, 10);
  } else {
    // Year omitted: infer the current year, but if that lands the date in
    // the FUTURE relative to `now` (day granularity, time-of-day ignored),
    // roll it back a year -- a yearless `since:`/`until:` typed today is
    // meant as a recent PAST date, not next year's (Item 12). Compare
    // zero-padded MM-DD lexically: both candidates share `now`'s year here,
    // so this is equivalent to a same-day date comparison. Strict `>` only
    // -- today's own date is valid and must NOT roll back. This never
    // touches a fully-specified `DD/MM/YYYY` value (that path is above).
    year = now.getFullYear();
    const candidateMd = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const nowMd = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (candidateMd > nowMd) {
      year = now.getFullYear() - 1;
    }
  }

  // new Date(year, month, 0) -- with `month` here the 1-based UK month --
  // lands on the last calendar day of that month (day 0 of the *next*
  // zero-indexed month is the last day of the intended one), correctly
  // accounting for leap years via the JS Date engine.
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/** Inverse of the ISO value chips carry -- formats `YYYY-MM-DD` as UK
 * `DD/MM/YYYY` for display (Omnibox's chip labels/tooltips). */
export function isoToUkDisplay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function buildChip(key: string, rawValue: string, full: string, now: Date): OmniChip | null {
  const chipKey = KEY_ALIASES[key.toLowerCase()];
  if (!chipKey) return null;

  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  switch (chipKey) {
    case 'from':
    case 'claim':
      return { key: chipKey, value: trimmed, raw: full, supported: true };

    case 'since':
    case 'until': {
      const iso = parseUkDate(trimmed, now);
      if (!iso) return null;
      return { key: chipKey, value: iso, raw: full, supported: true };
    }

    case 'assigned': {
      const value = trimmed.toLowerCase() === 'unassigned' ? 'unassigned' : trimmed;
      return { key: 'assigned', value, raw: full, supported: true };
    }

    case 'status': {
      const value = trimmed.toLowerCase();
      if (value !== 'pending' && value !== 'completed') return null;
      return { key: 'status', value, raw: full, supported: true };
    }

    case 'archived': {
      if (trimmed.toLowerCase() !== 'yes') return null;
      return { key: 'archived', value: 'yes', raw: full, supported: true };
    }

    default:
      return null;
  }
}

/** Parses a typed omnibox query string into recognised operator chips plus
 * the free-text remainder (destined for the `q` param). Pure/synchronous. */
export function parseOmniQuery(input: string, options: ParseOmniQueryOptions = {}): ParsedOmniQuery {
  const now = options.now ?? new Date();
  const chips: OmniChip[] = [];
  const freeTextParts: string[] = [];
  const invalidTokens: InvalidToken[] = [];

  // Array.from(...) rather than a for-of over the iterator directly -- this
  // repo's tsconfig target predates native iterator support for that
  // syntax (TS2802 without --downlevelIteration/es2015+).
  const matches = Array.from(input.trim().matchAll(TOKEN_RE));
  for (const match of matches) {
    const [full, qKey, qVal, kKey, kVal, bare] = match;
    if (bare !== undefined) {
      freeTextParts.push(bare);
      continue;
    }
    const key = qKey ?? kKey ?? '';
    const rawValue = qVal !== undefined ? qVal : (kVal ?? '');
    const chip = buildChip(key, rawValue, full, now);
    if (chip) {
      chips.push(chip);
    } else {
      freeTextParts.push(full);
      // Distinguish a mistyped recognised operator (`since:31/13`,
      // `status:bogus`, `archived:no`) from genuinely unknown free text
      // (`foo:bar`): only the former has a key present in KEY_ALIASES.
      // Both still land in freeText (unchanged behaviour), but the omnibox
      // uses invalidTokens to show corrective feedback for the former
      // (Item 13). A non-empty value is required so a bare `from:` -- which
      // the tokenizer already captures as a plain word, never reaching here
      // -- and an empty `from:""` aren't flagged as malformed operators.
      const recognisedKey = KEY_ALIASES[key.toLowerCase()];
      if (recognisedKey && rawValue.trim()) {
        invalidTokens.push({ raw: full, key: recognisedKey });
      }
    }
  }

  return { chips, freeText: freeTextParts.join(' '), invalidTokens };
}
